import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { ImageDisplay } from '../../components/ImageDisplay';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Kindergarten {
  _id: string;
  name: string;
  about: string;
  phone: string;
  location: {
    address: string;
    coordinates: {
      lat: number | null;
      lng: number | null;
    };
    photo: string;
  };
  photos: string[];
  teachers: Array<{
    fullName: string;
    position: string;
    photo: string;
  }>;
  responsibleAdmin?: {
    id: string;
    name: string;
    email: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface KindergartenFormData {
  name: string;
  about: string;
  phone: string;
  address: string;
  kindergartenPhotos: File[];
  existingPhotos: string[];
  locationPhoto: File | null;
  existingLocationPhoto: string;
  teachers: Array<{
    fullName: string;
    photo: File | null;
    existingPhoto: string;
    position: string;
  }>;
}

const Kindergartens: React.FC = () => {
  const { hasPermission, admin } = useAdminAuth();
  const [kindergartens, setKindergartens] = useState<Kindergarten[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingKindergarten, setEditingKindergarten] = useState<Kindergarten | null>(null);
  const [viewingKindergarten, setViewingKindergarten] = useState<Kindergarten | null>(null);
  const [formData, setFormData] = useState<KindergartenFormData>({
    name: '',
    about: '',
    phone: '',
    address: '',
    kindergartenPhotos: [],
    existingPhotos: [],
    locationPhoto: null,
    existingLocationPhoto: '',
    teachers: [{ fullName: '', photo: null, existingPhoto: '', position: 'Вихователь' }],
  });
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedKindergarten, setSelectedKindergarten] = useState<Kindergarten | null>(null);
  const [kindergartenAdmins, setKindergartenAdmins] = useState<any[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<string>('');

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchKindergartens();
    if (hasPermission('kindergartens', 'update')) {
      fetchKindergartenAdmins();
    }
  }, []);

  const fetchKindergartens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kindergartens');
      console.log('📥 Отримано дитсадки:', response.data.data.length);
      response.data.data.forEach((kg: any) => {
        console.log(`🏫 ${kg.name}:`, {
          photos: kg.photos?.length || 0,
          locationPhoto: kg.location?.photo ? 'є' : 'немає'
        });
      });
      setKindergartens(response.data.data || []);
    } catch (error: any) {
      console.error('Помилка при отриманні дитячих садків:', error);
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const fetchKindergartenAdmins = async () => {
    try {
      const response = await api.get('/admin-auth/kindergarten-admins');
      setKindergartenAdmins(response.data.data || []);
    } catch (error: any) {
      console.error('Помилка при отриманні адміністраторів:', error);
    }
  };

  const handleOpenDialog = (kindergarten?: Kindergarten) => {
    setError(''); // Очищаємо помилки
    
    if (kindergarten) {
      setEditingKindergarten(kindergarten);
      setFormData({
        name: kindergarten.name,
        about: kindergarten.about,
        phone: kindergarten.phone,
        address: kindergarten.location.address,
        kindergartenPhotos: [],
        existingPhotos: kindergarten.photos || [],
        locationPhoto: null,
        existingLocationPhoto: kindergarten.location.photo || '',
        teachers: kindergarten.teachers.map(t => ({ 
          fullName: t.fullName, 
          photo: null, 
          existingPhoto: t.photo || '',
          position: t.position 
        })),
      });
    } else {
      setEditingKindergarten(null);
      setFormData({
        name: '',
        about: '',
        phone: '',
        address: '',
        kindergartenPhotos: [],
        existingPhotos: [],
        locationPhoto: null,
        existingLocationPhoto: '',
        teachers: [{ fullName: '', photo: null, existingPhoto: '', position: 'Вихователь' }],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingKindergarten(null);
    setFormData({
      name: '',
      about: '',
      phone: '',
      address: '',
      kindergartenPhotos: [],
      existingPhotos: [],
      locationPhoto: null,
      existingLocationPhoto: '',
      teachers: [{ fullName: '', photo: null, existingPhoto: '', position: 'Вихователь' }],
    });
  };

  const handleOpenViewDialog = (kindergarten: Kindergarten) => {
    setViewingKindergarten(kindergarten);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingKindergarten(null);
  };

  const handleSubmit = async () => {
    try {
      // Валідація на фронтенді
      if (!formData.name || formData.name.length < 2) {
        setError('Назва повинна бути не менше 2 символів');
        return;
      }
      
      if (!formData.about || formData.about.length < 10) {
        setError('Опис повинен бути не менше 10 символів');
        return;
      }
      
      if (!formData.phone) {
        setError('Номер телефону обов\'язковий');
        return;
      }
      
      if (!formData.address || formData.address.length < 5) {
        setError('Адреса повинна бути не менше 5 символів');
        return;
      }

      const formDataToSend = new FormData();
      // Додаємо основні дані
      formDataToSend.append('name', formData.name);
      formDataToSend.append('about', formData.about);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('location[address]', formData.address);
      
      // Додаємо всі фото дитсадку
      formData.kindergartenPhotos.forEach((photo, index) => {
        formDataToSend.append('photos', photo);
      });
      
      // Додаємо фото локації окремо
      if (formData.locationPhoto) {
        formDataToSend.append('locationPhoto', formData.locationPhoto);
      }

      // Додаємо вчителів (тільки для POST запиту)
      if (!editingKindergarten) {
        const teachersData = formData.teachers.map(teacher => ({
          fullName: teacher.fullName,
          photo: '',
          position: teacher.position
        }));
        
        formDataToSend.append('teachers', JSON.stringify(teachersData));
      }
      
      // Додаємо фото вчителів
      formData.teachers.forEach((teacher, index) => {
        if (teacher.photo) {
          formDataToSend.append(`teacherPhotos[${index}]`, teacher.photo);
        }
      });

      // Логуємо дані для діагностики
      console.log('📤 Відправляємо дані:');
      console.log('📸 Фото дитсадку:', formData.kindergartenPhotos.length);
      console.log('📍 Фото локації:', formData.locationPhoto ? 'завантажено' : 'немає');
      formDataToSend.forEach((value, key) => {
        console.log(key, value);
      });

      if (editingKindergarten) {
        // Для оновлення додаємо teachers як JSON
        const teachersData = formData.teachers.map(teacher => ({
          fullName: teacher.fullName,
          photo: '',
          position: teacher.position
        }));
        formDataToSend.append('teachers', JSON.stringify(teachersData));
        
        await api.put(`/kindergartens/${editingKindergarten._id}`, formDataToSend);
      } else {
        await api.post('/kindergartens', formDataToSend);
      }

      handleCloseDialog();
      fetchKindergartens();
    } catch (error: any) {
      console.error('Помилка при збереженні:', error);
      console.error('Response data:', error.response?.data);
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        setError(`Помилки валідації: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Помилка збереження');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цей дитячий садок?')) {
      try {
        await api.delete(`/kindergartens/${id}`);
        fetchKindergartens();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення');
      }
    }
  };

  const addTeacher = () => {
    setFormData(prev => ({
      ...prev,
      teachers: [...prev.teachers, { fullName: '', photo: null, existingPhoto: '', position: 'Вихователь' }]
    }));
  };

  const removeTeacher = (index: number) => {
    setFormData(prev => ({
      ...prev,
      teachers: prev.teachers.filter((_, i) => i !== index)
    }));
  };

  const updateTeacher = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      teachers: prev.teachers.map((teacher, i) => 
        i === index ? { ...teacher, fullName: value, position: teacher.position } : teacher
      )
    }));
  };

  const handleAssignAdmin = async () => {
    if (!selectedKindergarten || !selectedAdminId) return;

    try {
      await api.patch(`/kindergartens/${selectedKindergarten._id}/assign-admin`, {
        adminId: selectedAdminId
      });
      
      setOpenAssignDialog(false);
      setSelectedKindergarten(null);
      setSelectedAdminId('');
      fetchKindergartens();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка призначення адміністратора');
    }
  };

  const handleRemoveAdmin = async (kindergartenId: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити призначення адміністратора?')) {
      try {
        await api.patch(`/kindergartens/${kindergartenId}/remove-admin`);
        fetchKindergartens();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Помилка видалення призначення');
      }
    }
  };

  const openAssignAdminDialog = (kindergarten: Kindergarten) => {
    setSelectedKindergarten(kindergarten);
    setSelectedAdminId('');
    setOpenAssignDialog(true);
  };

  // Перевіряємо чи адміністратор є kindergarten_admin
  const isKindergartenAdmin = admin?.role === 'kindergarten_admin';
  const currentKindergarten = kindergartens.find(kg => kg.responsibleAdmin === admin?.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Дитячі садки
          </Typography>
          {isKindergartenAdmin && currentKindergarten && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <InfoIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="primary">
                Ви відповідаєте за: <strong>{currentKindergarten.name}</strong>
              </Typography>
            </Box>
          )}
        </Box>
        {hasPermission('kindergartens', 'create') && !isKindergartenAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати дитячий садок
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Адреса</TableCell>
                  <TableCell>Вчителі</TableCell>
                  <TableCell>Фото</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kindergartens.map((kindergarten) => (
                  <TableRow key={kindergarten._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {kindergarten.name}
                        </Typography>
                        {kindergarten.responsibleAdmin && (
                          <Badge badgeContent="Відповідальний" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        {kindergarten.phone}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon fontSize="small" color="action" />
                        {kindergarten.location.address}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {kindergarten.teachers.map((teacher, index) => (
                          <Chip
                            key={index}
                            label={teacher.fullName}
                            size="small"
                            variant="outlined"
                            avatar={
                              teacher.photo ? (
                                <Avatar src={teacher.photo} sx={{ width: 16, height: 16 }} />
                              ) : (
                                <Avatar sx={{ width: 16, height: 16 }}>
                                  <PersonIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                              )
                            }
                          />
                        ))}
                        {kindergarten.teachers.length === 0 && (
                          <Chip label="Немає вчителів" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ImageDisplay
                        images={kindergarten.photos}
                        maxDisplay={3}
                        size={32}
                        showLocationIcon={true}
                        locationImage={kindergarten.location?.photo}
                        title="Фото дитсадку"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewDialog(kindergarten)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('kindergartens', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(kindergarten)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('kindergartens', 'update') && !isKindergartenAdmin && (
                          <Tooltip title={kindergarten.responsibleAdmin ? "Змінити адміністратора" : "Призначити адміністратора"}>
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => openAssignAdminDialog(kindergarten)}
                            >
                              <PersonIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('kindergartens', 'update') && !isKindergartenAdmin && kindergarten.responsibleAdmin && (
                          <Tooltip title="Видалити призначення адміністратора">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleRemoveAdmin(kindergarten._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('kindergartens', 'delete') && !isKindergartenAdmin && (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(kindergarten._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingKindergarten ? 'Редагувати дитячий садок' : 'Додати дитячий садок'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Назва дитячого садку"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Телефон"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Адреса"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Про нас"
              value={formData.about}
              onChange={(e) => setFormData(prev => ({ ...prev, about: e.target.value }))}
              margin="normal"
              multiline
              rows={4}
              required
            />
          </Box>
            
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Фото дитячого садку (необов'язково)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Можна завантажити кілька фото дитячого садку. При завантаженні нових фото вони замінять існуючі.
            </Typography>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setFormData(prev => ({ ...prev, kindergartenPhotos: files }));
              }}
            />
            {formData.kindergartenPhotos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Завантажені фото:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.kindergartenPhotos.map((file, index) => (
                    <Avatar
                      key={index}
                      src={URL.createObjectURL(file)}
                      sx={{ width: 60, height: 60 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {formData.existingPhotos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Існуючі фото:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.existingPhotos.map((photo, index) => (
                    <Avatar
                      key={index}
                      src={photo}
                      sx={{ width: 60, height: 60 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Фото локації (необов'язково)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Фото місцезнаходження дитячого садку. При завантаженні нового фото воно замінить існуюче.
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files ? e.target.files[0] : null;
                setFormData(prev => ({ ...prev, locationPhoto: file }));
              }}
            />
            {formData.locationPhoto && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Завантажене фото локації:
                </Typography>
                <Avatar
                  src={URL.createObjectURL(formData.locationPhoto)}
                  sx={{ width: 60, height: 60 }}
                />
              </Box>
            )}
            {formData.existingLocationPhoto && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Існуюче фото локації:
                </Typography>
                <Avatar
                  src={formData.existingLocationPhoto}
                  sx={{ width: 60, height: 60 }}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Вчителі
              </Typography>
              <Button onClick={addTeacher} variant="outlined" size="small">
                Додати вчителя
              </Button>
            </Box>
            
            {formData.teachers.map((teacher, index) => {
              return (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="Ім'я вчителя"
                  value={teacher.fullName}
                  onChange={(e) => updateTeacher(index, e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Фото вчителя (необов'язково). При завантаженні нового фото воно замінить існуюче.
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setFormData(prev => ({
                        ...prev,
                        teachers: prev.teachers.map((t, i) => 
                          i === index ? { ...t, photo: file } : t
                        )
                      }));
                    }}
                    style={{ width: '200px' }}
                  />
                  {teacher.photo && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Нове фото:
                      </Typography>
                      <Avatar
                        src={URL.createObjectURL(teacher.photo)}
                        sx={{ width: 40, height: 40 }}
                      />
                    </Box>
                  )}
                  {teacher.existingPhoto && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Існуюче фото:
                      </Typography>
                      <Avatar
                        src={teacher.existingPhoto}
                        sx={{ width: 40, height: 40 }}
                      />
                    </Box>
                  )}
                </Box>
                {formData.teachers.length > 1 && (
                  <IconButton 
                    onClick={() => removeTeacher(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            )})}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingKindergarten ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог призначення адміністратора */}
      <Dialog 
        open={openAssignDialog} 
        onClose={() => setOpenAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Призначити адміністратора до дитячого садку
        </DialogTitle>
        <DialogContent>
          {selectedKindergarten && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" color="primary">
                {selectedKindergarten.name}
              </Typography>
            </Box>
          )}
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Виберіть адміністратора</InputLabel>
            <Select
              value={selectedAdminId}
              onChange={(e) => setSelectedAdminId(e.target.value)}
              label="Виберіть адміністратора"
            >
              {kindergartenAdmins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.name} ({admin.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {kindergartenAdmins.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Немає доступних адміністраторів дитячих садків
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>
            Скасувати
          </Button>
          <Button 
            onClick={handleAssignAdmin} 
            variant="contained"
            disabled={!selectedAdminId}
          >
            Призначити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог перегляду дитячого садку */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Інформація про дитячий садок
        </DialogTitle>
        <DialogContent>
          {viewingKindergarten && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                  <SchoolIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {viewingKindergarten.name}
                  </Typography>
                  <Chip 
                    label={viewingKindergarten.isActive ? 'Активний' : 'Неактивний'} 
                    color={viewingKindergarten.isActive ? 'success' : 'default'} 
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Телефон
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    {viewingKindergarten.phone}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Адреса
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    {viewingKindergarten.location.address}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Опис
                </Typography>
                <Typography variant="body1">
                  {viewingKindergarten.about}
                </Typography>
              </Box>

              {viewingKindergarten.photos && viewingKindergarten.photos.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Фотографії дитячого садку
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {viewingKindergarten.photos.map((photo, index) => (
                        <Avatar
                          key={index}
                          src={photo}
                          sx={{ width: 60, height: 60 }}
                          variant="rounded"
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {viewingKindergarten.location.photo && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Фотографія локації
                    </Typography>
                    <Avatar
                      src={viewingKindergarten.location.photo}
                      sx={{ width: 120, height: 80 }}
                      variant="rounded"
                    />
                  </Box>
                </>
              )}

              {viewingKindergarten.teachers && viewingKindergarten.teachers.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Вчителі
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {viewingKindergarten.teachers.map((teacher, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={teacher.photo}
                            sx={{ width: 40, height: 40 }}
                          />
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {teacher.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {teacher.position}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {viewingKindergarten.responsibleAdmin && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Відповідальний адміністратор
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {viewingKindergarten.responsibleAdmin.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {viewingKindergarten.responsibleAdmin.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Додаткова інформація
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата створення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingKindergarten.createdAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Останнє оновлення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingKindergarten.updatedAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Kindergartens; 